'use client';

import React from 'react';
import { History, X, Clock, User, Layers, Sparkles, FileText, Code2 } from 'lucide-react';

interface InspectAuditLogModalProps {
  log: any | null;
  onClose: () => void;
  handleOpenWorkspaceBoards?: (wsId: string) => void;
  handleOpenBoardCards?: (boardId: string) => void;
  handleOpenCardDetail?: (cardId: string) => void;
}

export const InspectAuditLogModal: React.FC<InspectAuditLogModalProps> = ({
  log,
  onClose,
  handleOpenWorkspaceBoards,
  handleOpenBoardCards,
  handleOpenCardDetail,
}) => {
  if (!log) return null;

  return (
    <div className="fixed inset-0 z-[85] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-[#0D1122] border border-slate-800 rounded-3xl max-w-2xl w-full p-6 space-y-6 shadow-2xl relative max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-indigo-500/15 border border-indigo-500/30 text-indigo-400 flex items-center justify-center">
              <History className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-white flex items-center gap-2">
                <span>Audit Event Details</span>
                <span className="px-2.5 py-0.5 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded text-[10px] font-mono font-bold">
                  {log.action}
                </span>
              </h3>
              <p className="text-xs text-slate-400 font-mono flex items-center gap-1">
                <Clock className="w-3 h-3 text-slate-500" />
                <span>{new Date(log.createdAt).toLocaleString()}</span>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto pr-1 space-y-5">
          {/* Main summary */}
          <div className="bg-[#060811] border border-slate-800/80 rounded-2xl p-4 space-y-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Actor & Event Summary</span>
            <p className="text-sm font-semibold text-slate-200">
              <strong className="text-white">{log.actor?.name || 'Administrator'}</strong> ({log.actor?.email || log.actorId}) executed{' '}
              <span className="text-indigo-400 font-mono">{log.action}</span>
            </p>
          </div>

          {/* Key Identifiers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="bg-[#060811] p-3 rounded-2xl border border-slate-800 space-y-1">
              <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Event ID</span>
              <span className="text-slate-300 font-mono text-[11px] select-all">{log.id}</span>
            </div>

            <div className="bg-[#060811] p-3 rounded-2xl border border-slate-800 space-y-1">
              <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Actor ID</span>
              <span className="text-slate-300 font-mono text-[11px] select-all">{log.actorId || 'System'}</span>
            </div>

            <div className="bg-[#060811] p-3 rounded-2xl border border-slate-800 space-y-1">
              <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Workspace Scope</span>
              <span className="text-slate-300 font-mono text-[11px]">{log.workspaceId || 'Global (None)'}</span>
            </div>

            <div className="bg-[#060811] p-3 rounded-2xl border border-slate-800 space-y-1">
              <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Board / Card Scope</span>
              <span className="text-slate-300 font-mono text-[11px]">
                {log.boardId ? `Board: ${log.boardId}` : 'None'}{log.cardId ? ` / Card: ${log.cardId}` : ''}
              </span>
            </div>
          </div>

          {/* Drill-down Actions */}
          {(log.cardId || log.boardId || log.workspaceId) && (
            <div className="space-y-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Related Entity Inspection</span>
              <div className="flex flex-wrap gap-2">
                {log.cardId && handleOpenCardDetail && (
                  <button
                    onClick={() => {
                      onClose();
                      handleOpenCardDetail(log.cardId);
                    }}
                    className="px-3 py-2 bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/40 text-indigo-300 font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <Sparkles className="w-4 h-4 text-indigo-400" />
                    <span>Inspect Card &rarr;</span>
                  </button>
                )}

                {log.boardId && handleOpenBoardCards && (
                  <button
                    onClick={() => {
                      onClose();
                      handleOpenBoardCards(log.boardId);
                    }}
                    className="px-3 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/40 text-purple-300 font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <Layers className="w-4 h-4 text-purple-400" />
                    <span>Inspect Board &rarr;</span>
                  </button>
                )}

                {log.workspaceId && handleOpenWorkspaceBoards && (
                  <button
                    onClick={() => {
                      onClose();
                      handleOpenWorkspaceBoards(log.workspaceId);
                    }}
                    className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <Layers className="w-4 h-4 text-slate-400" />
                    <span>Inspect Workspace &rarr;</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Event Metadata */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
              <Code2 className="w-4 h-4 text-indigo-400" />
              <span>Event Metadata Payload</span>
            </h4>
            <div className="bg-[#04060E] border border-slate-800/90 rounded-2xl p-4 font-mono text-xs text-indigo-300 overflow-x-auto max-h-48">
              <pre>{log.meta ? JSON.stringify(log.meta, null, 2) : '{}'}</pre>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-4 border-t border-slate-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs rounded-xl transition-all cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
